import {type ConnectionOperation, OPTIMISTIC_KEY} from '@github-ui/optimistic-connection'
import {sanitize} from '@github-ui/sanitize'

import type {LabelNode} from '../repository-label-index-page-query-types'

/**
 * `labels` is a Relay `edges[].node` connection, which the engine wraps/unwraps natively, so these
 * operations carry plain label nodes.
 */

export const LABELS_CONNECTION_PATH = ['labels']

/** Prefix for a client-only optimistic label id, until the server assigns a real one. */
export const TEMP_LABEL_ID_PREFIX = 'temp-new-label-'

export type LabelInput = {name: string; description: string; color: string}
type LabelMutationNode = Omit<LabelNode, 'archivedAt'>
type OptimisticLabelNode = Omit<LabelNode, 'fullDatabaseId' | 'url'>

export function newTempLabelId(): string {
  return `${TEMP_LABEL_ID_PREFIX}${crypto.randomUUID()}`
}

/** Names render as sanitized HTML (`nameHTML`). */
function sanitizeLabelName(name: string): string {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-custom-sanitize-config --
     Existing custom sanitizer config; migrate to a named preset or VerifiedHTML when changed. */
  return sanitize(name, {ALLOWED_TAGS: [], ALLOWED_ATTR: [], ALLOW_ARIA_ATTR: false, RETURN_TRUSTED_TYPE: false})
}

function buildLabelNode(id: string, input: LabelInput): OptimisticLabelNode {
  return {
    id,
    name: input.name,
    nameHTML: sanitizeLabelName(input.name),
    color: input.color,
    description: input.description,
    archivedAt: null,
  }
}

const labelCounts = (delta: number) => [{path: [...LABELS_CONNECTION_PATH, 'totalCount'], delta}]
const activeLabelCounts = (delta: number) => [
  ...labelCounts(delta),
  {path: [...LABELS_CONNECTION_PATH, 'unarchivedCount'], delta},
]

export function buildCreateLabelOperation(tempId: string, input: LabelInput): ConnectionOperation {
  return {
    type: 'addToConnection',
    connectionPath: LABELS_CONNECTION_PATH,
    node: buildLabelNode(tempId, input),
    anchor: {at: 'start'},
    counts: activeLabelCounts(1),
  }
}

/** Tags the confirmed label with the temp id so the still-pending optimistic add dedupes against it. */
export function buildConfirmedCreateLabelOperation(tempId: string, label: LabelMutationNode): ConnectionOperation {
  return {
    type: 'addToConnection',
    connectionPath: LABELS_CONNECTION_PATH,
    node: {...label, archivedAt: null, [OPTIMISTIC_KEY]: tempId},
    anchor: {at: 'start'},
    counts: activeLabelCounts(1),
  }
}

export function buildEditLabelOperation(labelId: string, input: LabelInput): ConnectionOperation {
  return {
    type: 'setFields',
    id: labelId,
    fields: {
      name: input.name,
      nameHTML: sanitizeLabelName(input.name),
      color: input.color,
      description: input.description,
    },
  }
}

/**
 * Recovers the raw input the write needs from the operation the overlay carries. Colocated with the
 * builders above so a field rename can't silently drop values from the request.
 */
export function labelInputFromOperation(operation: ConnectionOperation): LabelInput | undefined {
  const source =
    operation.type === 'addToConnection'
      ? operation.node
      : operation.type === 'setFields'
        ? operation.fields
        : undefined
  if (!source) return undefined

  const {name, description, color} = source as Partial<LabelInput>
  if (typeof name !== 'string' || typeof description !== 'string' || typeof color !== 'string') return undefined
  return {name, description, color}
}

export function buildConfirmedEditLabelOperation(labelId: string, label: LabelMutationNode): ConnectionOperation {
  return {
    type: 'setFields',
    id: labelId,
    fields: {name: label.name, nameHTML: label.nameHTML, color: label.color, description: label.description},
  }
}

export function buildChangeLabelArchiveStateOperation(labelId: string, isArchived: boolean): ConnectionOperation {
  return {
    type: 'removeFromConnection',
    connectionPath: LABELS_CONNECTION_PATH,
    nodeId: labelId,
    counts: [
      ...labelCounts(-1),
      {path: [...LABELS_CONNECTION_PATH, 'archivedCount'], delta: isArchived ? -1 : 1},
      {path: [...LABELS_CONNECTION_PATH, 'unarchivedCount'], delta: isArchived ? 1 : -1},
    ],
  }
}

export function buildRemoveLabelFromCurrentViewOperation(labelId: string, isArchived: boolean): ConnectionOperation {
  return {
    type: 'removeFromConnection',
    connectionPath: LABELS_CONNECTION_PATH,
    nodeId: labelId,
    counts: [
      ...labelCounts(-1),
      {path: [...LABELS_CONNECTION_PATH, isArchived ? 'archivedCount' : 'unarchivedCount'], delta: -1},
    ],
  }
}
